/**
 * Catalogues français du **contenu de jeu**, en lecture seule.
 *
 * Le back-office affiche des cartes, des niveaux et des effets : il doit montrer le nom et la
 * description que verra le joueur, pas la clé `content.carte.carte_feu_001.name`. Les catalogues
 * livrés par `@elemora/content` sont donc importés tels quels (aucune copie, aucune
 * retranscription) et résolus à l'affichage.
 *
 * Ce module ne modifie rien : l'édition porte sur les identifiants de clé (`nameKey`), ce qui
 * laisse la traduction au contenu — le back-office reste un outil d'édition de structure.
 */
import fr from '@elemora/content/i18n/fr.json';

/** Préfixe commun à toutes les clés de contenu (DECISIONS §9.2). */
const PREFIXE = 'content.';

/** Vue dictionnaire du catalogue : les clés sont dynamiques, le JSON est imbriqué. */
const CATALOGUE = fr as unknown as Record<string, unknown>;

/**
 * Résout une clé pointée (`content.carte.carte_feu_001.name`) dans le catalogue importé.
 *
 * Renvoie `null` si la clé est absente : l'appelant décide alors d'afficher la clé brute, ce qui
 * est l'information utile pour un administrateur (une clé absente est un défaut de contenu).
 */
export function resoudreCle(cle: string): string | null {
  const sansPrefixe = cle.startsWith(PREFIXE) ? cle.slice(PREFIXE.length) : cle;
  const segments = sansPrefixe.split('.');
  let courant: unknown = CATALOGUE;
  for (const segment of segments) {
    if (typeof courant !== 'object' || courant === null) return null;
    courant = (courant as Record<string, unknown>)[segment];
  }
  return typeof courant === 'string' ? courant : null;
}

/** Nom lisible d'une entité de contenu, ou sa clé si la traduction manque. */
export function libelleContenu(cle: string): string {
  return resoudreCle(cle) ?? cle;
}

/** Nom lisible d'une carte, avec repli explicite sur la clé. */
export function nomCarte(carte: { nameKey: string }): string {
  return libelleContenu(carte.nameKey);
}

/** Nom lisible d'un niveau. */
export function nomNiveau(niveau: { nameKey: string }): string {
  return libelleContenu(niveau.nameKey);
}
